import { useCallback, useRef, useState } from 'react'
import { ApiError, api } from '../../lib/api'

/**
 * Pengunggah berpotong dengan ukuran adaptif dan pengiriman paralel.
 *
 * Dua hal yang menentukan kecepatan unggahan, dan versi lama tidak melakukan
 * keduanya:
 *
 *  1. UKURAN POTONGAN. Potongan 10 MB tetap untuk semua kondisi berarti pada
 *     koneksi kantor yang cepat, sebagian besar waktu habis untuk overhead
 *     per-permintaan; sedangkan pada data seluler yang lambat, satu potongan
 *     bisa memakan semenit dan kegagalan di ujungnya mahal sekali diulang.
 *     Di sini ukurannya menyesuaikan diri ke laju yang terukur, dengan
 *     sasaran sekitar empat detik per potongan.
 *
 *  2. PARALEL. Satu potongan pada satu waktu berarti setiap jeda pulang-pergi
 *     ke server adalah waktu ketika tidak ada byte yang mengalir. Mengirim
 *     beberapa potongan bersamaan menutup jeda itu, dan pada koneksi dengan
 *     latensi tinggi bedanya besar.
 *
 * Konkurensi dibatasi secara global, bukan per berkas: tanpa itu, tiga berkas
 * yang diunggah bersamaan akan membuka dua belas koneksi sekaligus dan justru
 * saling memperlambat.
 */

export type StatusUnggah = 'antre' | 'berjalan' | 'selesai' | 'gagal' | 'dibatalkan'

export interface ItemUnggah {
  id: string
  file: File
  status: StatusUnggah
  /** 0–100 */
  progres: number
  /** Byte per detik, terukur. */
  kecepatan: number
  /** Perkiraan sisa detik. */
  sisa: number
  galat?: string
  namaAkhir?: string
}

export interface TargetUnggah {
  upload_type?: 'userdata' | 'shared' | 'nas'
  subdir?: string
  token?: string
  target_dir?: string
  target_path?: string
}

/** Berkas yang diproses bersamaan. */
const MAKS_BERKAS_PARALEL = 3

/** Permintaan potongan yang boleh melayang bersamaan, di seluruh aplikasi. */
const MAKS_POTONGAN_SERENTAK = 6

/** Potongan paralel untuk satu berkas. */
const PARALEL_PER_BERKAS = 4

/** Sasaran durasi satu potongan. Cukup lama untuk menutup overhead
 *  per-permintaan, cukup singkat agar kegagalan tidak mahal diulang. */
const SASARAN_DETIK = 4

const MIN_POTONGAN = 1 * 1024 * 1024
const MAKS_POTONGAN = 64 * 1024 * 1024

/** Berkas di bawah ambang ini dikirim sekali jalan, tanpa dipotong. */
const AMBANG_POTONG = 8 * 1024 * 1024

const MAKS_PERCOBAAN = 3

/**
 * Pembatas konkurensi global.
 *
 * Dibuat di tingkat modul, bukan di dalam hook, supaya batasnya berlaku untuk
 * seluruh aplikasi — termasuk ketika halaman berkas dan halaman share
 * sama-sama sedang mengunggah.
 */
class Gerbang {
  #aktif = 0
  readonly #antre: (() => void)[] = []

  constructor(private readonly batas: number) {}

  async pakai<T>(fn: () => Promise<T>): Promise<T> {
    if (this.#aktif >= this.batas) {
      await new Promise<void>((lepas) => this.#antre.push(lepas))
    }
    this.#aktif++
    try {
      return await fn()
    } finally {
      this.#aktif--
      this.#antre.shift()?.()
    }
  }
}

const gerbang = new Gerbang(MAKS_POTONGAN_SERENTAK)

/** Tebakan awal sebelum ada satu pun pengukuran. */
function ukuranAwal(ukuranBerkas: number): number {
  if (ukuranBerkas <= AMBANG_POTONG) return ukuranBerkas
  // 8 MB adalah titik tengah yang wajar: cukup besar untuk koneksi kantor,
  // belum menyakitkan pada data seluler.
  return 8 * 1024 * 1024
}

function jepit(n: number): number {
  return Math.max(MIN_POTONGAN, Math.min(MAKS_POTONGAN, Math.round(n)))
}

const tidur = (ms: number) => new Promise((r) => setTimeout(r, ms))

export function useUploader(onSelesaiSemua?: () => void) {
  const [antrean, setAntrean] = useState<ItemUnggah[]>([])
  const kontrol = useRef(new Map<string, AbortController>())
  const berjalan = useRef(0)
  const menunggu = useRef<{ item: ItemUnggah; target: TargetUnggah }[]>([])
  const selesaiRef = useRef(onSelesaiSemua)
  selesaiRef.current = onSelesaiSemua

  const perbarui = useCallback((id: string, ubah: Partial<ItemUnggah>) => {
    setAntrean((lama) => lama.map((i) => (i.id === id ? { ...i, ...ubah } : i)))
  }, [])

  const berikutnyaRef = useRef<() => void>(() => {})

  const jalankan = useCallback(
    async (item: ItemUnggah, target: TargetUnggah) => {
      berjalan.current++
      const ac = new AbortController()
      kontrol.current.set(item.id, ac)
      perbarui(item.id, { status: 'berjalan', progres: 0 })

      try {
        const sesi = await api.uploads.init({
          filename: item.file.name,
          size: item.file.size,
          ...target,
        })

        // ── Keadaan bersama antar pekerja ────────────────────────────────
        let offsetBerikut = 0
        let indeksBerikut = 0
        let terkirim = 0
        let ukuranPotongan = ukuranAwal(item.file.size)
        const mulai = performance.now()

        /** Ambil satu tugas potongan; null berarti berkasnya sudah habis. */
        const ambilTugas = (): { indeks: number; awal: number; panjang: number } | null => {
          if (offsetBerikut >= item.file.size) return null
          const awal = offsetBerikut
          const panjang = Math.min(ukuranPotongan, item.file.size - awal)
          const indeks = indeksBerikut++
          offsetBerikut = awal + panjang
          return { indeks, awal, panjang }
        }

        const pekerja = async () => {
          for (;;) {
            if (ac.signal.aborted) throw new DOMException('Dibatalkan', 'AbortError')

            const tugas = ambilTugas()
            if (!tugas) return

            const potongan = item.file.slice(tugas.awal, tugas.awal + tugas.panjang)

            // Percobaan ulang dengan jeda menaik. Pada data seluler, satu
            // potongan gagal jauh lebih sering daripada seluruh unggahan
            // benar-benar tidak mungkin diselesaikan.
            let percobaan = 0
            for (;;) {
              try {
                await gerbang.pakai(() =>
                  api.uploads.chunk(sesi.upload_id, tugas.indeks, potongan, ac.signal),
                )
                break
              } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') throw err
                // Galat 4xx berarti permintaannya sendiri yang salah; mengulang
                // tidak akan menolong dan hanya menunda pesan galatnya.
                if (err instanceof ApiError && err.status >= 400 && err.status < 500) throw err
                if (++percobaan >= MAKS_PERCOBAAN) throw err
                await tidur(500 * 2 ** (percobaan - 1))
              }
            }

            terkirim += tugas.panjang

            /*
             * Laju diukur secara agregat — total byte selesai dibagi waktu
             * berjalan — bukan dari durasi satu potongan. Dengan pengiriman
             * paralel, potongan-potongan berbagi bandwidth, sehingga durasi
             * masing-masing bukan gambaran laju yang sebenarnya.
             */
            const detik = (performance.now() - mulai) / 1000
            const laju = detik > 0 ? terkirim / detik : 0

            if (laju > 0) {
              // Bandwidth dibagi antar pekerja, jadi jatah satu potongan
              // adalah laju total dibagi jumlah pekerja.
              const sasaran = (laju / PARALEL_PER_BERKAS) * SASARAN_DETIK
              // Perubahan dihaluskan agar ukuran tidak melonjak-lonjak
              // mengikuti gejolak sesaat.
              ukuranPotongan = jepit(ukuranPotongan * 0.6 + sasaran * 0.4)
            }

            perbarui(item.id, {
              progres: Math.round((terkirim / item.file.size) * 90),
              kecepatan: laju,
              sisa: laju > 0 ? (item.file.size - terkirim) / laju : 0,
            })
          }
        }

        const jumlahPekerja =
          item.file.size <= AMBANG_POTONG ? 1 : PARALEL_PER_BERKAS
        await Promise.all(Array.from({ length: jumlahPekerja }, pekerja))

        // 95%: potongan sudah terkirim semua, tetapi server masih menggabungkan.
        // Menampilkan 100% di sini membuat jeda berikutnya terasa macet.
        perbarui(item.id, { progres: 95 })
        const hasil = await api.uploads.finalize(sesi.upload_id)
        perbarui(item.id, {
          status: 'selesai',
          progres: 100,
          namaAkhir: hasil.filename,
          sisa: 0,
        })

        // Item sukses menghilang sendiri; item gagal tetap tinggal agar
        // alasannya sempat terbaca. Asimetri ini disengaja.
        setTimeout(() => setAntrean((lama) => lama.filter((x) => x.id !== item.id)), 5000)
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          perbarui(item.id, { status: 'dibatalkan' })
        } else {
          perbarui(item.id, {
            status: 'gagal',
            galat: err instanceof ApiError ? err.message : 'Unggahan gagal.',
          })
        }
      } finally {
        kontrol.current.delete(item.id)
        berjalan.current--
        berikutnyaRef.current()
      }
    },
    [perbarui],
  )

  const berikutnya = useCallback(() => {
    while (berjalan.current < MAKS_BERKAS_PARALEL && menunggu.current.length > 0) {
      const n = menunggu.current.shift()
      if (n) void jalankan(n.item, n.target)
    }
    if (berjalan.current === 0 && menunggu.current.length === 0) {
      selesaiRef.current?.()
    }
  }, [jalankan])

  berikutnyaRef.current = berikutnya

  const tambah = useCallback(
    (files: File[], target: TargetUnggah) => {
      if (files.length === 0) return

      const stempel = Date.now()
      const baru = files.map<ItemUnggah>((file, i) => ({
        id: `${stempel}-${i}-${file.name}`,
        file,
        status: 'antre',
        progres: 0,
        kecepatan: 0,
        sisa: 0,
      }))

      setAntrean((lama) => [...lama, ...baru])
      menunggu.current.push(...baru.map((item) => ({ item, target })))
      berikutnya()
    },
    [berikutnya],
  )

  const batalkan = useCallback((id: string) => {
    kontrol.current.get(id)?.abort()
    menunggu.current = menunggu.current.filter((n) => n.item.id !== id)
    setAntrean((lama) =>
      lama.map((i) => (i.id === id && i.status === 'antre' ? { ...i, status: 'dibatalkan' } : i)),
    )
  }, [])

  const bersihkan = useCallback(() => {
    setAntrean((lama) => lama.filter((i) => i.status === 'berjalan' || i.status === 'antre'))
  }, [])

  const adaYangBerjalan = antrean.some((i) => i.status === 'berjalan' || i.status === 'antre')

  return { antrean, tambah, batalkan, bersihkan, adaYangBerjalan }
}
